/**
 * Page List Component
 *
 * Displays and manages the list of custom thank you pages (CPT).
 *
 * @since 1.0.0
 */

import { Card } from '@redfoxwp/core-components/components/card';
import { Flex } from '@redfoxwp/core-components/components/flex';
import { PostList, type PostListStrings } from '@redfoxwp/core-components/components/post-list';
import { TabHeader } from '@redfoxwp/core-components/components/tab-header';
import { __ } from '@wordpress/i18n';
import { addQueryArgs } from '@wordpress/url';
import { useState } from 'react';

import { useDebounce, useThankYouPages } from '../../hooks';

/**
 * Page List component
 *
 * @since 1.0.0
 *
 * @return JSX element.
 */
export const PageList = (): JSX.Element => {
	const [ searchTerm, setSearchTerm ] = useState< string >( '' );

	// Debounce search term with 500ms delay and minimum 3 characters
	const { debouncedValue: debouncedSearchTerm } = useDebounce( {
		value: searchTerm,
		delay: 500,
		minLength: 3,
	} );

	const {
		pages,
		loading,
		pagination,
		deletePage,
		goToPage,
		changeItemsPerPage,
	} = useThankYouPages( {
		search: debouncedSearchTerm,
		postsPerPage: 10,
	} );

	const handleAddNewPage = (): void => {
		window.location.href = 'post-new.php?post_type=redfoxwp_thank_you';
	};

	const handleEditPage = ( pageId: number ): void => {
		window.location.href = `post.php?post=${ pageId }&action=edit`;
	};

	const handleDeletePage = async ( pageId: number ): Promise< void > => {
		await deletePage( pageId );
	};

	const handleDuplicatePage = ( pageId: number ): void => {
		// Get nonce and admin URL from localized script data
		const duplicateNonce = window.redfoxThankYouSettings?.duplicateNonce ?? '';
		const adminUrl = window.redfoxThankYouSettings?.adminUrl ?? '/wp-admin/';

		// Navigate to duplicate with nonce
		const duplicateUrl = addQueryArgs( `${ adminUrl }post-new.php`, {
			post_type: 'redfoxwp_thank_you',
			duplicate: pageId.toString(),
			_wpnonce: duplicateNonce,
		} );

		window.location.href = duplicateUrl;
	};

	// Localization strings for the PostList component
	const strings: PostListStrings = {
		searchPlaceholder: __( 'Search thank you pages…', 'redfox-thank-you' ),
		addNewButton: __( 'Add New Page', 'redfox-thank-you' ),
		loadingText: __( 'Loading…', 'redfox-thank-you' ),
		noPostsFound: __( 'No thank you pages found.', 'redfox-thank-you' ),
		noSearchResults: __( 'Try adjusting your search terms or', 'redfox-thank-you' ),
		clearSearch: __( 'clear the search', 'redfox-thank-you' ),
		createFirstPost: __( 'creating your first thank you page', 'redfox-thank-you' ),
		editLabel: __( 'Edit', 'redfox-thank-you' ),
		deleteModalTitle: __( 'Delete Thank You Page', 'redfox-thank-you' ),
		deleteModalMessage: __( 'Are you sure you want to delete this thank you page? This action cannot be undone.', 'redfox-thank-you' ),
		deleteConfirmButton: __( 'Delete', 'redfox-thank-you' ),
		cancelButton: __( 'Cancel', 'redfox-thank-you' ),
		modifiedPrefix: __( 'Modified:', 'redfox-thank-you' ),
		statusLabels: {
			publish: __( 'Published', 'redfox-thank-you' ),
			draft: __( 'Draft', 'redfox-thank-you' ),
			pending: __( 'Pending', 'redfox-thank-you' ),
			private: __( 'Private', 'redfox-thank-you' ),
			future: __( 'Scheduled', 'redfox-thank-you' ),
			trash: __( 'Trash', 'redfox-thank-you' ),
		},
		pagination: {
			noItems: __( 'No items', 'redfox-thank-you' ),
			oneItem: __( '1 item', 'redfox-thank-you' ),
			showing: __( 'Showing', 'redfox-thank-you' ),
			of: __( 'of', 'redfox-thank-you' ),
			items: __( 'items', 'redfox-thank-you' ),
			firstPage: __( 'First page', 'redfox-thank-you' ),
			goToFirstPage: __( 'Go to first page', 'redfox-thank-you' ),
			previousPage: __( 'Previous page', 'redfox-thank-you' ),
			goToPreviousPage: __( 'Go to previous page', 'redfox-thank-you' ),
			nextPage: __( 'Next page', 'redfox-thank-you' ),
			goToNextPage: __( 'Go to next page', 'redfox-thank-you' ),
			lastPage: __( 'Last page', 'redfox-thank-you' ),
			goToLastPage: __( 'Go to last page', 'redfox-thank-you' ),
			page: __( 'Page', 'redfox-thank-you' ),
			goToPage: __( 'Go to page', 'redfox-thank-you' ),
			itemsPerPage: __( 'Items per page:', 'redfox-thank-you' ),
		},
		modalStrings: {
			closeModal: __( 'Close modal', 'redfox-thank-you' ),
		},
	};

	return (
		<Flex direction='column' gap='30px' fullWidth>
			{/* Page header with title and description */}
			<TabHeader
				title={ __( 'Thank You Pages', 'redfox-thank-you' ) }
				description={ __(
					'Manage individual thank you pages. You can create or edit custom thank you pages.',
					'redfox-thank-you',
				) }
			/>

			{/* Main content */}
			<Card styleProps={ { padding: '30px', radius: '10px', background: '#fff' } }>
				<PostList
					posts={ pages }
					strings={ strings }
					loading={ loading }
					searchTerm={ searchTerm }
					pagination={ pagination ?? undefined }
					actions={ { edit: true, delete: true, duplicate: true, preview: false } }
					onSearch={ setSearchTerm }
					onAddNew={ handleAddNewPage }
					onEdit={ handleEditPage }
					onDelete={ handleDeletePage }
					onDuplicate={ handleDuplicatePage }
					onPageChange={ goToPage }
					onItemsPerPageChange={ changeItemsPerPage }
					showItemsPerPageSelector={ true }
					itemsPerPageOptions={ [ 10, 20, 50, 100 ] }
					maxPageButtons={ 7 }
					showPageInfo={ true }
					showFirstLastButtons={ true }
				/>
			</Card>
		</Flex>
	);
};
